import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Message, Translation } from '../types';
import { User, Activity, Volume2, Square } from 'lucide-react';
import { useVoice } from '../hooks/useVoice';
import { TriageBadge } from './TriageBadge';

interface ChatMessageProps {
  message: Message;
  translations: Translation;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message, translations }) => {
  const isUser = message.role === 'user';
  const { speakText, cancelSpeech, isSpeaking } = useVoice(message.language, false);

  const handleSpeak = () => {
    if (isSpeaking) {
      cancelSpeech();
    } else {
      speakText(message.content);
    }
  };

  return (
    <div className={`flex w-full mb-8 ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`flex max-w-[90%] md:max-w-[85%] ${isUser ? 'flex-row-reverse' : 'flex-row'} gap-4`}>
        {/* Avatar */}
        <div
          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center border shadow-lg ${
            isUser
              ? 'bg-neutral-800 border-white/10 text-brand-400'
              : 'bg-brand-900/30 border-brand-500/30 text-brand-400'
          }`}
        >
          {isUser ? <User size={20} /> : <Activity size={20} />}
        </div>

        {/* Bubble */}
        <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} w-full`}>
          <div
            className={`px-6 py-5 rounded-2xl shadow-md text-sm md:text-base leading-relaxed w-full ${
              isUser
                ? 'bg-neutral-800 border border-white/10 text-gray-100 rounded-tr-sm'
                : 'bg-neutral-900 border border-white/10 text-gray-200 rounded-tl-sm'
            }`}
          >
            {isUser ? (
              <p>{message.content}</p>
            ) : (
              <div className="markdown-content">
                {/* Triage badge at the top of AI messages */}
                {message.triageLevel && (
                  <TriageBadge level={message.triageLevel} language={message.language} />
                )}

                <ReactMarkdown
                  components={{
                    ul:     ({ node, ...props }) => <ul className="list-disc ml-4 my-3 text-gray-300" {...props} />,
                    ol:     ({ node, ...props }) => <ol className="list-decimal ml-4 my-3 text-gray-300" {...props} />,
                    li:     ({ node, ...props }) => <li className="mb-2 pl-1" {...props} />,
                    strong: ({ node, ...props }) => <span className="font-bold text-brand-300" {...props} />,
                    p:      ({ node, ...props }) => <p className="mb-4 last:mb-0" {...props} />,
                    h3:     ({ node, ...props }) => (
                      <h3 className="font-serif font-bold text-xl text-white mb-3 mt-6 border-b border-white/10 pb-2" {...props} />
                    ),
                    hr: () => <hr className="border-white/10 my-4" />,
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center mt-2 ml-1 gap-4">
            <span className="text-xs text-gray-600 font-medium">
              {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>

            {!isUser && (
              <button
                onClick={handleSpeak}
                className={`flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider transition-all px-2 py-1 rounded hover:bg-white/5 ${
                  isSpeaking ? 'text-brand-400' : 'text-gray-500 hover:text-brand-400'
                }`}
                title="Read Aloud"
              >
                {isSpeaking ? (
                  <>
                    <Square size={10} className="fill-current animate-pulse" />
                    <span>Stop</span>
                  </>
                ) : (
                  <>
                    <Volume2 size={12} />
                    <span>Listen</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
